/**
 * Duck for subscription management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { types as sdkTypes } from '../util/sdkLoader';

// Action types
export const CREATE_SUBSCRIPTION_REQUEST = 'app/subscription/CREATE_SUBSCRIPTION_REQUEST';
export const CREATE_SUBSCRIPTION_SUCCESS = 'app/subscription/CREATE_SUBSCRIPTION_SUCCESS';
export const CREATE_SUBSCRIPTION_ERROR = 'app/subscription/CREATE_SUBSCRIPTION_ERROR';

// Initial state
const initialState = {
  createSubscriptionInProgress: false,
  createSubscriptionError: null,
  checkoutSession: null,
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  
  switch (type) {
    case CREATE_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        createSubscriptionInProgress: true,
        createSubscriptionError: null,
        checkoutSession: null,
      };
    
    case CREATE_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        createSubscriptionInProgress: false,
        createSubscriptionError: null,
        checkoutSession: payload,
      };
    
    case CREATE_SUBSCRIPTION_ERROR:
      return {
        ...state,
        createSubscriptionInProgress: false,
        createSubscriptionError: payload,
        checkoutSession: null,
      };
    
    default:
      return state;
  }
}

// Action creators
export const createSubscriptionRequest = () => ({
  type: CREATE_SUBSCRIPTION_REQUEST,
});

export const createSubscriptionSuccess = (checkoutSession) => ({
  type: CREATE_SUBSCRIPTION_SUCCESS,
  payload: checkoutSession,
});

export const createSubscriptionError = (error) => ({
  type: CREATE_SUBSCRIPTION_ERROR,
  payload: error,
});

// Thunk action for creating subscription using existing Stripe infrastructure
export const createSubscription = (params) => (dispatch, getState, sdk) => {
  console.log('createSubscription thunk called with params:', params);
  dispatch(createSubscriptionRequest());
  
  const { priceId, userId, userEmail, planTier } = params;
  
  // Create a checkout session using Stripe's API directly
  // This follows the same pattern as the existing checkout functionality
  return fetch('/api/create-subscription-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      priceId,
      userId,
      userEmail,
      planTier,
      successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/subscription/cancel`,
    }),
  })
    .then(response => {
      console.log('API response status:', response.status);
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      return response.json();
    })
    .then(data => {
      console.log('API response data:', data);
      dispatch(createSubscriptionSuccess(data));
      
      // Redirect to Stripe Checkout
      if (data.url) {
        console.log('Redirecting to Stripe URL:', data.url);
        window.location.href = data.url;
      } else {
        console.error('No URL returned from API');
        throw new Error('No checkout URL returned');
      }
      
      return data;
    })
    .catch(error => {
      console.error('Error creating subscription:', error);
      dispatch(createSubscriptionError(error.message));
      throw error;
    });
};
